import type { ThemeConfig } from 'antd';

const fontSans = '"Onest", "Noto Sans", "Segoe UI", sans-serif';
const fontMono = '"JetBrains Mono", "SFMono-Regular", Consolas, "Liberation Mono", monospace';

export const lightTheme: ThemeConfig = {
  token: {
    colorPrimary: '#d36a2f',
    colorSuccess: '#2b7a73',
    colorWarning: '#e08c2f',
    colorError: '#c3412f',
    colorInfo: '#2f5fd4',

    colorText: '#1c2431',
    colorTextSecondary: '#394456',
    colorTextTertiary: '#6b778c',
    colorTextQuaternary: '#9aa7ba',
    colorTextHeading: '#1c2431',
    colorTextDescription: '#6b778c',
    colorTextLabel: '#394456',
    colorTextLightSolid: '#ffffff',

    colorBgBase: '#f6f1e8',
    colorBgLayout: '#f6f1e8',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#fffaf2',
    colorBgSpotlight: '#1c2431',
    colorBgMask: 'rgba(22, 30, 44, 0.18)',

    colorBorder: 'rgba(28, 36, 49, 0.12)',
    colorBorderSecondary: 'rgba(28, 36, 49, 0.08)',
    colorSplit: 'rgba(28, 36, 49, 0.08)',

    colorLink: '#2f5fd4',
    colorLinkHover: '#244fc0',
    colorLinkActive: '#1b3e9b',

    controlOutline: 'rgba(211, 106, 47, 0.2)',

    borderRadius: 12,
    borderRadiusLG: 16,
    borderRadiusSM: 10,

    fontFamily: fontSans,
    fontFamilyCode: fontMono,
    fontSize: 14,

    marginXS: 8,
    marginSM: 12,
    margin: 16,
    marginMD: 20,
    marginLG: 24,
    marginXL: 32,

    boxShadow: '0 16px 40px rgba(20, 28, 43, 0.16)',
    boxShadowSecondary: '0 26px 70px rgba(20, 28, 43, 0.14)',
  },
  components: {
    Layout: {
      headerBg: '#fffaf2',
      siderBg: '#fffaf2',
      bodyBg: '#f6f1e8',
      headerHeight: 64,
    },
    Menu: {
      itemBg: 'transparent',
      itemColor: '#394456',
      itemHoverColor: '#1c2431',
      itemHoverBg: 'rgba(211, 106, 47, 0.12)',
      itemSelectedColor: '#1c2431',
      itemSelectedBg: 'rgba(211, 106, 47, 0.18)',
      itemBorderRadius: 10,
      subMenuItemBg: 'transparent',
      groupTitleColor: '#6b778c',
    },
    Table: {
      headerBg: '#fffaf2',
      rowHoverBg: 'rgba(211, 106, 47, 0.08)',
    },
    Card: {
      paddingLG: 24,
      headerBg: '#fffaf2',
    },
    Button: {
      primaryShadow: '0 10px 22px rgba(211, 106, 47, 0.22)',
    },
    Modal: {
      contentBg: '#fffaf2',
      headerBg: '#fffaf2',
      footerBg: '#fffaf2',
      titleColor: '#1c2431',
    },
    Popover: {
      colorBgElevated: '#fffaf2',
      colorText: '#1c2431',
    },
    Dropdown: {
      paddingBlock: 6,
    },
    Notification: {
      colorBgElevated: '#fffaf2',
      colorSuccessBg: 'rgba(43, 122, 115, 0.12)',
      colorInfoBg: 'rgba(47, 95, 212, 0.12)',
      colorWarningBg: 'rgba(211, 106, 47, 0.14)',
      colorErrorBg: 'rgba(195, 65, 47, 0.12)',
    },
    Message: {
      contentBg: '#fffaf2',
      contentPadding: '10px 12px',
    },
  },
};

export const darkTheme: ThemeConfig = {
  token: {
    colorPrimary: '#4f8cff',
    colorSuccess: '#2dd4bf',
    colorWarning: '#f59e0b',
    colorError: '#fb7185',
    colorInfo: '#8ab4ff',

    colorText: '#f5f7fa',
    colorTextSecondary: '#c3cad8',
    colorTextTertiary: '#94a3b8',
    colorTextQuaternary: '#7b879b',
    colorTextHeading: '#f5f7fa',
    colorTextDescription: '#aab4c5',
    colorTextLabel: '#c3cad8',
    colorTextLightSolid: '#ffffff',

    colorBgBase: '#0f1115',
    colorBgLayout: '#0f1115',
    colorBgContainer: '#141414',
    colorBgElevated: '#1f1f1f',
    colorBgSpotlight: '#0b1220',
    colorBgMask: 'rgba(6, 10, 20, 0.55)',

    colorBorder: 'rgba(255, 255, 255, 0.12)',
    colorBorderSecondary: 'rgba(255, 255, 255, 0.08)',
    colorSplit: 'rgba(255, 255, 255, 0.08)',

    colorLink: '#8ab4ff',
    colorLinkHover: '#adc8ff',
    colorLinkActive: '#6a9cff',

    controlOutline: 'rgba(79, 140, 255, 0.25)',

    borderRadius: 12,
    borderRadiusLG: 16,
    borderRadiusSM: 10,

    fontFamily: fontSans,
    fontFamilyCode: fontMono,
    fontSize: 14,

    marginXS: 8,
    marginSM: 12,
    margin: 16,
    marginMD: 20,
    marginLG: 24,
    marginXL: 32,

    boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
    boxShadowSecondary: '0 26px 70px rgba(0, 0, 0, 0.55)',
  },
  components: {
    Layout: {
      headerBg: '#111827',
      siderBg: '#0f172a',
      bodyBg: '#0f1115',
      headerHeight: 64,
    },
    Menu: {
      itemBg: 'transparent',
      itemColor: '#c3cad8',
      itemHoverColor: '#ffffff',
      itemHoverBg: 'rgba(79, 140, 255, 0.15)',
      itemSelectedColor: '#ffffff',
      itemSelectedBg: 'rgba(79, 140, 255, 0.22)',
      itemBorderRadius: 10,
      subMenuItemBg: 'transparent',
      groupTitleColor: '#94a3b8',
      darkItemBg: 'transparent',
      darkSubMenuItemBg: 'transparent',
      darkItemColor: '#c3cad8',
      darkItemHoverColor: '#ffffff',
      darkItemHoverBg: 'rgba(79, 140, 255, 0.15)',
      darkItemSelectedColor: '#ffffff',
      darkItemSelectedBg: 'rgba(79, 140, 255, 0.22)',
      darkGroupTitleColor: '#94a3b8',
    },
    Table: {
      headerBg: '#141a28',
      rowHoverBg: 'rgba(255, 255, 255, 0.06)',
    },
    Card: {
      paddingLG: 24,
      headerBg: '#141a28',
    },
    Button: {
      primaryShadow: '0 10px 22px rgba(79, 140, 255, 0.35)',
    },
    Modal: {
      contentBg: '#1f1f1f',
      headerBg: '#1a1a1a',
      footerBg: '#1a1a1a',
      titleColor: '#f5f7fa',
    },
    Popover: {
      colorBgElevated: '#1f1f1f',
      colorText: '#f5f7fa',
    },
    Dropdown: {
      paddingBlock: 6,
    },
    Notification: {
      colorBgElevated: '#1f1f1f',
      colorSuccessBg: 'rgba(45, 212, 191, 0.15)',
      colorInfoBg: 'rgba(138, 180, 255, 0.15)',
      colorWarningBg: 'rgba(245, 158, 11, 0.18)',
      colorErrorBg: 'rgba(251, 113, 133, 0.16)',
    },
    Message: {
      contentBg: '#1f1f1f',
      contentPadding: '10px 12px',
    },
  },
};

// Production status colors
export const statusColors = {
  pending: '#d9d9d9',
  scheduled: '#2f54eb',
  in_progress: '#1890ff',
  queue: '#d9d9d9',
  checking: '#faad14',
  printing: '#1890ff',
  cutting: '#faad14',
  assembly: '#722ed1',
  packing: '#13c2c2',
  quality_check: '#fa8c16',
  postprint: '#722ed1',
  completed: '#52c41a',
  failed: '#ff4d4f',
  on_hold: '#faad14',
  cancelled: '#ff4d4f',
  paused: '#faad14',
  ready: '#52c41a',
  shipped: '#8c8c8c',
} as const;

// Order status colors
export const orderStatusColors = {
  draft: '#d9d9d9',
  pending: '#faad14',
  confirmed: '#1890ff',
  in_production: '#722ed1',
  ready: '#52c41a',
  shipped: '#13c2c2',
  completed: '#52c41a',
  cancelled: '#ff4d4f',
} as const;

// Payment status colors
export const paymentStatusColors = {
  pending: '#faad14',
  partial: '#1890ff',
  paid: '#52c41a',
  overdue: '#ff4d4f',
} as const;
